import {
  Case,
  OutputDocument,
  OutputDocumentVersion,
  OutputDocumentVersionStatus,
  OutputDocumentType,
  Clinician,
  PageSummariesResponse,
  DocumentPageSegmentation,
  PaginatedCasesResponse,
  SandboxConfigStatus,
  SandboxGenerationRun,
  OutputDocumentChatMessage,
  SandboxSkill,
  SandboxSkillProposal,
  SandboxSkillProposalStatus,
  SandboxSkillScope,
  SandboxTemplate,
} from "@/types";
import Ajax from "./Ajax";

export let baseUrl = "";
if (import.meta.env.VITE_ENV === "prod") {
  baseUrl = "https://api.mdley.co";
}

export const getCases = async (
  accessToken: string | null,
  page: number = 1,
  pageSize: number = 10,
  search?: string,
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  clinicianIds?: string[]
) => {
  let response = null;
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) {
      params.append("search", search);
    }
    if (sortBy) {
      params.append("sort_by", sortBy);
    }
    if (sortOrder) {
      params.append("sort_order", sortOrder);
    }
    clinicianIds?.forEach((clinicianId) => {
      params.append("clinician_id", clinicianId);
    });
    response = await Ajax.req<PaginatedCasesResponse>({
      url: `${baseUrl}/api/v1/case/all?${params.toString()}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getCase = async (caseId: string, accessToken: string | null) => {
  let response = null;
  try {
    response = await Ajax.req<Case>({
      url: `${baseUrl}/api/v1/case/${caseId}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const createCase = async (
  firstName: string,
  lastName: string,
  clinicianId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<Case>({
      url: `${baseUrl}/api/v1/case/create`,
      method: "POST",
      body: {
        first_name: firstName,
        last_name: lastName,
        clinician_id: clinicianId,
      },
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const uploadItems = async (
  caseId: string,
  formData: FormData,
  accessToken: string | null
) => {
  let response = true;
  try {
    await Ajax.req({
      url: `${baseUrl}/api/v1/doc/upload/${caseId}`,
      method: "POST",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      body: formData,
      accessToken,
    });
  } catch (error) {
    response = false;
    console.error(error);
  }
  return response;
};

export const createOutputDocument = async (
  caseId: string,
  docTypeId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocument>({
      url: `${baseUrl}/api/v1/doc/create-output-document/${caseId}/${docTypeId}`,
      method: "POST",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const regenerateOutputDocument = async (
  documentId: string,
  accessToken: string | null,
  userInstructions?: string
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocument>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/regenerate`,
      method: "POST",
      body: userInstructions?.trim()
        ? { user_instructions: userInstructions.trim() }
        : undefined,
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getDocumentTypes = async (
  specialty: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocumentType[]>({
      url: `${baseUrl}/api/v1/doc/output-document-types/${specialty}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getOutputDocument = async (
  documentId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<{ document: OutputDocument }>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const reProcessInputDocument = async (
  documentId: string,
  accessToken: string | null
) => {
  let response = true;
  try {
    await Ajax.req({
      url: `${baseUrl}/api/v1/doc/input-document/reprocess/${documentId}`,
      method: "POST",
      accessToken,
    });
  } catch (error) {
    console.error(error);
    response = false;
  }
  return response;
};

export const deleteInputDocument = async (
  documentId: string,
  accessToken: string | null
) => {
  let response = true;
  try {
    await Ajax.req({
      url: `${baseUrl}/api/v1/doc/input-document/${documentId}`,
      method: "DELETE",
      accessToken,
    });
  } catch (error) {
    console.error(error);
    response = false;
  }
  return response;
};

export const updateInputDocumentAssociation = async (
  inputDocumentId: string,
  recordReview: boolean | null,
  qmeSections: boolean | null,
  supplementalReport: boolean | null,
  accessToken: string | null
) => {
  let response = true;
  try {
    await Ajax.req({
      url: `${baseUrl}/api/v1/doc/input-document/update-association/${inputDocumentId}`,
      method: "POST",
      body: {
        record_review: recordReview,
        qme_sections: qmeSections,
        supplemental_report: supplementalReport,
      },
      accessToken,
    });
  } catch (error) {
    console.error(error);
    response = false;
  }
  return response;
};

export const deleteCase = async (
  caseId: string,
  accessToken: string | null
) => {
  let response = true;
  try {
    await Ajax.req({
      url: `${baseUrl}/api/v1/case/${caseId}`,
      method: "DELETE",
      accessToken,
    });
  } catch (error) {
    console.error(error);
    response = false;
  }
  return response;
};

const downloadDocument = async (
  requestUrl: string,
  token: string | null,
  display: boolean = true
) => {
  try {
    const response = await Ajax.req<{ filename: string; blob: Blob }>({
      url: requestUrl,
      method: "GET",
      accessToken: token,
    });

    const filename = response.filename;
    const blob = response.blob;

    const url = window.URL.createObjectURL(blob);
    if (display) {
      return url;
    }

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return "downloaded";
  } catch (error) {
    console.error("Error downloading document:", error);
    return null;
  }
};

export const downloadInputDocument = async (
  documentId: string,
  accessToken: string | null,
  display: boolean = false
) => {
  return downloadDocument(
    `${baseUrl}/api/v1/doc/input-document/download/${documentId}`,
    accessToken,
    display
  );
};

export const getOutputDocumentVersions = async (
  documentId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocumentVersion[]>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/versions`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const previewOutputDocumentVersion = async (
  documentId: string,
  versionId: string,
  accessToken: string | null
) => {
  return downloadDocument(
    `${baseUrl}/api/v1/doc/output-document/${documentId}/versions/${versionId}/preview`,
    accessToken,
    true
  );
};

export const downloadOutputDocumentVersion = async (
  documentId: string,
  versionId: string,
  accessToken: string | null
) => {
  return downloadDocument(
    `${baseUrl}/api/v1/doc/output-document/${documentId}/versions/${versionId}/download`,
    accessToken,
    false
  );
};

export const downloadOutputDocumentVersionPreview = async (
  documentId: string,
  versionId: string,
  accessToken: string | null
) => {
  return downloadDocument(
    `${baseUrl}/api/v1/doc/output-document/${documentId}/versions/${versionId}/preview`,
    accessToken,
    false
  );
};

export const activateOutputDocumentVersion = async (
  documentId: string,
  versionId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocumentVersion>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/versions/${versionId}/active`,
      method: "POST",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const uploadOutputDocumentVersion = async (
  documentId: string,
  file: File,
  status: OutputDocumentVersionStatus,
  accessToken: string | null
) => {
  let response = null;
  try {
    const formData = new FormData();
    formData.append("file", file);
    response = await Ajax.req<OutputDocumentVersion>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/versions/upload?status=${status}&active=true`,
      method: "POST",
      body: formData,
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const updateOutputDocumentVersionStatus = async (
  documentId: string,
  versionId: string,
  status: OutputDocumentVersionStatus,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocumentVersion>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/versions/${versionId}/status`,
      method: "POST",
      body: { status },
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const chatEditOutputDocument = async (
  documentId: string,
  userInstructions: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<{ run_id: string }>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/chat-edit`,
      method: "POST",
      body: { user_instructions: userInstructions },
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getSandboxGenerationRun = async (
  documentId: string,
  runId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxGenerationRun>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/sandbox-runs/${runId}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getOutputDocumentChatMessages = async (
  documentId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<OutputDocumentChatMessage[]>({
      url: `${baseUrl}/api/v1/doc/output-document/${documentId}/chat-messages`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getInputDocumentPageSummaries = async (
  documentId: string,
  page: number = 1,
  pageSize: number = 10,
  accessToken: string | null,
  segmentationId?: string,
  clinicalInfo?: boolean,
  medicationInfo?: boolean,
  previousEvaluation?: boolean
) => {
  let response = null;
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (segmentationId) {
      params.append("segmentation_id", segmentationId);
    }
    if (clinicalInfo !== undefined) {
      params.append("clinical_info", clinicalInfo.toString());
    }
    if (medicationInfo !== undefined) {
      params.append("medication_info", medicationInfo.toString());
    }
    if (previousEvaluation !== undefined) {
      params.append("previous_evaluation", previousEvaluation.toString());
    }

    response = await Ajax.req<PageSummariesResponse>({
      url: `${baseUrl}/api/v1/doc/input-document/page-summaries/${documentId}?${params.toString()}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getInputDocumentPageSegmentations = async (
  documentId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<DocumentPageSegmentation[]>({
      url: `${baseUrl}/api/v1/doc/input-document/page-segmentations/${documentId}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getClinicians = async (accessToken: string | null) => {
  let response = null;
  try {
    response = await Ajax.req<Clinician[]>({
      url: `${baseUrl}/api/v1/user/clinicians`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getClinicianById = async (
  clinicianId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<Clinician>({
      url: `${baseUrl}/api/v1/user/clinician/${clinicianId}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getClinicianSignature = async (
  clinicianId: string,
  accessToken: string | null
) => {
  let url = null;
  try {
    const response = await Ajax.req<{ filename: string; blob: Blob }>({
      url: `${baseUrl}/api/v1/user/clinician/signature/${clinicianId}`,
      method: "GET",
      accessToken,
    });
    url = window.URL.createObjectURL(response.blob);
  } catch (error) {
    console.error(error);
  }
  return url;
};

export const updateClinician = async (
  clinicianId: string,
  name: string,
  shortName: string,
  specialty: string,
  accessToken: string | null,
  signature?: File
) => {
  let response = null;
  try {
    const formData = new FormData();
    formData.append("clinician_id", clinicianId);
    formData.append("name", name);
    formData.append("short_name", shortName);
    formData.append("specialty", specialty);
    if (signature) {
      formData.append("signature", signature);
    }

    response = await Ajax.req<Clinician>({
      url: `${baseUrl}/api/v1/user/clinician`,
      method: "POST",
      accessToken,
      body: formData,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getCliniciansNotSetup = async (accessToken: string | null) => {
  let response = null;
  try {
    response = await Ajax.req<{ id: string; email: string }[]>({
      url: `${baseUrl}/api/v1/user/clinicians/not-setup`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getSandboxSkills = async (accessToken: string | null) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxSkill[]>({
      url: `${baseUrl}/api/v1/sandbox-config/skills`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const createSandboxSkill = async (
  payload: {
    name: string;
    scope: SandboxSkillScope;
    content: string;
    status: SandboxConfigStatus;
    specialty?: string | null;
    output_document_type_id?: string | null;
    clinician_id?: string | null;
  },
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxSkill>({
      url: `${baseUrl}/api/v1/sandbox-config/skills`,
      method: "POST",
      body: payload,
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const updateSandboxSkillStatus = async (
  skillId: string,
  status: SandboxConfigStatus,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxSkill>({
      url: `${baseUrl}/api/v1/sandbox-config/skills/${skillId}/status`,
      method: "PATCH",
      body: { status },
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const updateSandboxSkill = async (
  skillId: string,
  payload: {
    content: string;
  },
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxSkill>({
      url: `${baseUrl}/api/v1/sandbox-config/skills/${skillId}`,
      method: "PATCH",
      body: payload,
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getSandboxTemplates = async (accessToken: string | null) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxTemplate[]>({
      url: `${baseUrl}/api/v1/sandbox-config/templates`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getSandboxSkillDocuments = async (
  skillId: string,
  accessToken: string | null
) => {
  let response = null;
  try {
    const params = new URLSearchParams({
      sandbox_skill_id: skillId,
    });
    response = await Ajax.req<SandboxTemplate[]>({
      url: `${baseUrl}/api/v1/sandbox-config/templates?${params.toString()}`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const uploadSandboxTemplate = async (
  payload: {
    name: string;
    file: File;
    status: SandboxConfigStatus;
    specialty?: string;
    outputDocumentTypeId?: string;
    clinicianId?: string;
  },
  accessToken: string | null
) => {
  let response = null;
  try {
    const formData = new FormData();
    formData.append("file", payload.file);
    const params = new URLSearchParams({
      name: payload.name,
      status: payload.status,
    });
    if (payload.specialty) params.append("specialty", payload.specialty);
    if (payload.outputDocumentTypeId) {
      params.append("output_document_type_id", payload.outputDocumentTypeId);
    }
    if (payload.clinicianId) params.append("clinician_id", payload.clinicianId);
    response = await Ajax.req<SandboxTemplate>({
      url: `${baseUrl}/api/v1/sandbox-config/templates?${params.toString()}`,
      method: "POST",
      body: formData,
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const uploadSandboxSkillDocument = async (
  payload: {
    skillId: string;
    file: File;
    name?: string;
  },
  accessToken: string | null
) => {
  let response = null;
  try {
    const formData = new FormData();
    formData.append("file", payload.file);
    const params = new URLSearchParams();
    if (payload.name) {
      params.append("name", payload.name);
    }
    response = await Ajax.req<SandboxTemplate>({
      url: `${baseUrl}/api/v1/sandbox-config/skills/${payload.skillId}/documents?${params.toString()}`,
      method: "POST",
      body: formData,
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const deleteSandboxSkillDocument = async (
  skillId: string,
  documentId: string,
  accessToken: string | null
) => {
  try {
    await Ajax.req({
      url: `${baseUrl}/api/v1/sandbox-config/skills/${skillId}/documents/${documentId}`,
      method: "DELETE",
      accessToken,
    });
    return true;
  } catch (error) {
    console.error(error);
  }
  return false;
};

export const downloadSandboxSkillDocument = async (
  skillId: string,
  documentId: string,
  accessToken: string | null
) => {
  return downloadDocument(
    `${baseUrl}/api/v1/sandbox-config/skills/${skillId}/documents/${documentId}/download`,
    accessToken,
    false
  );
};

export const updateSandboxTemplateStatus = async (
  templateId: string,
  status: SandboxConfigStatus,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxTemplate>({
      url: `${baseUrl}/api/v1/sandbox-config/templates/${templateId}/status`,
      method: "PATCH",
      body: { status },
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const getSandboxSkillProposals = async (accessToken: string | null) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxSkillProposal[]>({
      url: `${baseUrl}/api/v1/sandbox-config/skill-proposals`,
      method: "GET",
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};

export const reviewSandboxSkillProposal = async (
  proposalId: string,
  status: SandboxSkillProposalStatus,
  accessToken: string | null
) => {
  let response = null;
  try {
    response = await Ajax.req<SandboxSkillProposal>({
      url: `${baseUrl}/api/v1/sandbox-config/skill-proposals/${proposalId}/review`,
      method: "POST",
      body: { status },
      accessToken,
    });
  } catch (error) {
    console.error(error);
  }
  return response;
};
